import { getSelector, rgbToHex } from "./domUtils";
import { getPageHtml } from "./editorApi";

const pageHtmlCache = new Map();
const EDITOR_PREVIEW_STYLE = `
<style id="editor-preview-fixes">
  html[data-editor-preview="true"] {
    background: #f8fafc;
  }

  html[data-editor-preview="true"] body {
    background: transparent !important;
  }

  html[data-editor-preview="true"] img[data-editor-hydrated],
  html[data-editor-preview="true"] picture[data-editor-hydrated] img {
    opacity: 1 !important;
    visibility: visible !important;
  }

  html[data-editor-preview="true"] [data-editor-hero-fallback="true"] {
    background:
      linear-gradient(180deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.98)) !important;
    min-height: min(70vh, 820px);
  }

  html[data-editor-preview="true"] [data-editor-hero-fallback="true"]::before,
  html[data-editor-preview="true"] [data-editor-hero-fallback="true"]::after {
    background: transparent !important;
  }

  html[data-editor-preview="true"] slideshow-carousel,
  html[data-editor-preview="true"] slideshow-component,
  html[data-editor-preview="true"] custom-slideshow,
  html[data-editor-preview="true"] .slideshow,
  html[data-editor-preview="true"] .slideshow__slide,
  html[data-editor-preview="true"] .content-over-media,
  html[data-editor-preview="true"] .content-over-media > *:first-child,
  html[data-editor-preview="true"] .content-over-media > picture,
  html[data-editor-preview="true"] .content-over-media > img,
  html[data-editor-preview="true"] .content-over-media picture img,
  html[data-editor-preview="true"] .slideshow__slide picture,
  html[data-editor-preview="true"] .slideshow__slide img,
  html[data-editor-preview="true"] .slideshow__slide video,
  html[data-editor-preview="true"] .slideshow__slide iframe {
    display: block !important;
  }

  html[data-editor-preview="true"] slideshow-carousel,
  html[data-editor-preview="true"] slideshow-component,
  html[data-editor-preview="true"] custom-slideshow,
  html[data-editor-preview="true"] .slideshow,
  html[data-editor-preview="true"] .slideshow__slide {
    visibility: visible !important;
    opacity: 1 !important;
    background-color: transparent !important;
  }

  html[data-editor-preview="true"] .slideshow,
  html[data-editor-preview="true"] .slideshow__slide,
  html[data-editor-preview="true"] .content-over-media {
    min-height: min(70vh, 820px);
  }

  html[data-editor-preview="true"] .content-over-media {
    position: relative !important;
    overflow: hidden !important;
    background-color: #f8fafc !important;
  }

  html[data-editor-preview="true"] .slideshow__slide picture,
  html[data-editor-preview="true"] .slideshow__slide img,
  html[data-editor-preview="true"] .slideshow__slide video,
  html[data-editor-preview="true"] .slideshow__slide iframe,
  html[data-editor-preview="true"] .content-over-media > picture,
  html[data-editor-preview="true"] .content-over-media > img,
  html[data-editor-preview="true"] .content-over-media > *:first-child:is(picture, img, video, iframe) {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    z-index: 0 !important;
  }

  html[data-editor-preview="true"] .slideshow__slide picture img,
  html[data-editor-preview="true"] .slideshow__slide > img,
  html[data-editor-preview="true"] .slideshow__slide video,
  html[data-editor-preview="true"] .slideshow__slide iframe,
  html[data-editor-preview="true"] .content-over-media picture img,
  html[data-editor-preview="true"] .content-over-media > img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    object-position: center center !important;
    opacity: 1 !important;
    visibility: visible !important;
    transform: none !important;
    filter: none !important;
  }

  html[data-editor-preview="true"] .slideshow__slide-content,
  html[data-editor-preview="true"] .content-over-media > :not(picture):not(img) {
    position: relative !important;
    z-index: 1 !important;
  }

  html[data-editor-preview="true"] .slideshow__slide[hidden],
  html[data-editor-preview="true"] .slideshow__slide[aria-hidden="true"] {
    display: block !important;
  }
</style>`;

const isDisallowedRootSelector = (selector) => {
  const s = selector.trim().toLowerCase();
  return (
    s === "html" ||
    s === "body" ||
    /^html:nth-of-type\(\d+\)$/.test(s) ||
    /^body:nth-of-type\(\d+\)$/.test(s)
  );
};

export const applyVariationToIframe = (iframe, variationJson) => {
  if (!iframe?.contentDocument) return;

  const doc = iframe.contentDocument;
  variationJson.forEach((v) => {
    if (!v?.selector || isDisallowedRootSelector(v.selector)) return;
    const el = doc.querySelector(v.selector);
    if (el) {
      if (v.content !== undefined) el.innerText = v.content;
      if (v.styles?.color) el.style.setProperty("color", v.styles.color, "important");
      if (v.styles?.backgroundColor) {
        el.style.setProperty("background-color", v.styles.backgroundColor, "important");
      }
    }
  });
};

const injectIntoHead = (html, snippet) => {
  if (html.includes("<head>")) return html.replace("<head>", `<head>${snippet}`);
  return `${snippet}${html}`;
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const renderLoadError = (iframe, pageUrl, message) => {
  if (!iframe) return;

  iframe.srcdoc = `
    <!doctype html>
    <html>
      <head>
        <base target="_blank">
        <style>
          body {
            margin: 0;
            min-height: 100vh;
            display: grid;
            place-items: center;
            background: #f8fafc;
            color: #111827;
            font-family: Arial, sans-serif;
          }
          main {
            width: min(680px, calc(100% - 32px));
            border: 1px solid #d8e2ef;
            border-radius: 8px;
            background: #ffffff;
            padding: 24px;
            box-shadow: 0 12px 28px rgba(15, 23, 42, 0.1);
          }
          h1 {
            margin: 0 0 12px;
            font-size: 22px;
          }
          p {
            margin: 8px 0;
            line-height: 1.5;
          }
          code {
            display: block;
            margin-top: 12px;
            padding: 12px;
            border-radius: 6px;
            background: #f1f5f9;
            color: #334155;
            overflow-wrap: anywhere;
          }
        </style>
      </head>
      <body>
        <main>
          <h1>Page could not be loaded in the editor</h1>
          <p>The editor fetched the client page but could not render a usable preview.</p>
          <p>Some websites block server-side fetches, require JavaScript to build the page, or send security rules that prevent embedded previews.</p>
          <code>${escapeHtml(pageUrl)}</code>
          <code>${escapeHtml(message || "Unknown load error")}</code>
        </main>
      </body>
    </html>`;
};

const sanitizeInlineStyleValue = (value) =>
  value
    .replace(/rgba\(\s*NaN\s*,\s*NaN\s*,\s*NaN\s*,\s*([^)]+)\)/gi, "rgba(0, 0, 0, $1)")
    .replace(/rgb\(\s*NaN\s*,\s*NaN\s*,\s*NaN\s*\)/gi, "rgb(0, 0, 0)")
    .replace(/\bNaN\b/g, "0");

const normalizeBrokenInlineStyles = (doc) => {
  doc.querySelectorAll("[style]").forEach((el) => {
    const style = el.getAttribute("style");
    if (!style || !/NaN/.test(style)) return;
    el.setAttribute("style", sanitizeInlineStyleValue(style));
  });
};

const hydrateLazyMedia = (doc) => {
  const attrPairs = [
    ["data-src", "src"],
    ["data-lazy-src", "src"],
    ["data-original", "src"],
    ["data-srcset", "srcset"],
    ["data-lazy-srcset", "srcset"],
    ["data-sizes", "sizes"],
  ];

  doc.querySelectorAll("img, source, iframe, video").forEach((el) => {
    let hydrated = false;

    attrPairs.forEach(([from, to]) => {
      const nextValue = el.getAttribute(from);
      const currentValue = el.getAttribute(to);
      if (!nextValue || currentValue) return;
      el.setAttribute(to, nextValue);
      hydrated = true;
    });

    if (hydrated) el.setAttribute("data-editor-hydrated", "true");
  });
};

const restoreNoscriptMedia = (doc) => {
  doc.querySelectorAll("noscript").forEach((noscript) => {
    const html = noscript.textContent?.trim();
    if (!html || !/<(img|picture|video|iframe)\b/i.test(html)) return;

    const parent = noscript.parentElement;
    if (!parent) return;
    if (parent.querySelector("img, picture, video, iframe")) return;

    const wrapper = doc.createElement("div");
    wrapper.innerHTML = html;
    const mediaNode = wrapper.querySelector("picture, img, video, iframe");
    if (!mediaNode) return;

    const clone = mediaNode.cloneNode(true);
    clone.setAttribute("data-editor-hydrated", "true");
    parent.insertBefore(clone, noscript);
  });
};

const hasRenderableMedia = (root) => {
  const media = root.querySelectorAll("img, picture img, video, iframe");
  return Array.from(media).some((el) => {
    const src = el.getAttribute("src") || el.getAttribute("srcset") || "";
    return Boolean(src && src.trim());
  });
};

const repairHeroFallbacks = (doc) => {
  const heroSelectors = [
    ".content-over-media",
    ".slideshow",
    ".slideshow__slide",
    "[class*='hero']",
    "[class*='banner']",
  ];

  const seen = new Set();
  doc.querySelectorAll(heroSelectors.join(", ")).forEach((el) => {
    if (seen.has(el)) return;
    seen.add(el);

    const style = el.getAttribute("style") || "";
    const hasBlackFallback =
      /--background:\s*0\s+0\s+0\b/i.test(style) ||
      /background-color:\s*rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)/i.test(style);

    if (!hasBlackFallback || hasRenderableMedia(el)) return;

    el.setAttribute("data-editor-hero-fallback", "true");
    el.style.setProperty("background-color", "#f8fafc", "important");
    el.style.setProperty("color", "#111827", "important");
  });
};

const forceStaticHeroRendering = (doc) => {
  const heroRoots = doc.querySelectorAll(
    "slideshow-carousel, slideshow-component, custom-slideshow, .slideshow, .slideshow__slide, .content-over-media"
  );

  heroRoots.forEach((root) => {
    const media = root.querySelector("picture img, img, video, iframe");
    if (!media) return;

    root.removeAttribute("hidden");
    root.setAttribute("aria-hidden", "false");
    root.style.setProperty("display", "block", "important");
    root.style.setProperty("visibility", "visible", "important");
    root.style.setProperty("opacity", "1", "important");

    if (root.matches(".slideshow, .slideshow__slide, .content-over-media")) {
      root.style.setProperty("background-color", "transparent", "important");
      root.style.setProperty("min-height", "min(70vh, 820px)", "important");
    }

    media.closest("picture")?.setAttribute("data-editor-hydrated", "true");
    media.setAttribute("data-editor-hydrated", "true");
    media.removeAttribute("hidden");
    media.setAttribute("aria-hidden", "false");
    media.setAttribute("loading", "eager");
    media.style.setProperty("display", "block", "important");
    media.style.setProperty("visibility", "visible", "important");
    media.style.setProperty("opacity", "1", "important");
    media.style.setProperty("object-fit", "cover", "important");
    media.style.setProperty("object-position", "center center", "important");

    const slide = root.closest(".slideshow__slide") || root;
    slide.removeAttribute("hidden");
    slide.setAttribute("aria-hidden", "false");
    slide.style.setProperty("display", "block", "important");
    slide.style.setProperty("visibility", "visible", "important");
    slide.style.setProperty("opacity", "1", "important");
    slide.style.setProperty("background-color", "transparent", "important");
    slide.style.setProperty("min-height", "min(70vh, 820px)", "important");
    slide.removeAttribute("data-editor-hero-fallback");
  });
};

const preparePreviewDocument = (doc) => {
  doc.documentElement.setAttribute("data-editor-preview", "true");
  normalizeBrokenInlineStyles(doc);
  hydrateLazyMedia(doc);
  restoreNoscriptMedia(doc);
  repairHeroFallbacks(doc);
  forceStaticHeroRendering(doc);
};

export const loadPage = async ({
  iframe,
  pageUrl,
  forceRefresh = false,
  allowSelection,
  shouldApply = false,
  step,
  variationJson,
  selectedElRef,
  setSelector,
  setContent,
  setTextColor,
  setBgColor,
  setGoalSelector,
  onBeforeElementChange,
}) => {
  if (!iframe || !pageUrl) return;

  try {
    let html = pageHtmlCache.get(pageUrl);
    if (!html || forceRefresh) {
      html = await getPageHtml(pageUrl);
      pageHtmlCache.set(pageUrl, html);
    }

    const sanitizedHtml = html
      .replace(/<meta\b[^>]*http-equiv=["']?content-security-policy["']?[^>]*>/gi, "")
      .replace(/<style[^>]*id=["']ab-prehide["'][^>]*>[\s\S]*?<\/style>/gi, "");

    const baseTag = `<base href="${pageUrl}">`;
    iframe.onload = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;

      preparePreviewDocument(doc);

      doc.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (allowSelection) {
            if (selectedElRef.current) selectedElRef.current.style.outline = "";
            if (step === 1 && typeof onBeforeElementChange === "function") {
              onBeforeElementChange();
            }
            const el = e.target;
            const sel = getSelector(el);
            if (isDisallowedRootSelector(sel)) {
              selectedElRef.current = null;
              return;
            }
            selectedElRef.current = el;
            el.style.outline = "2px solid #2563eb";
            setSelector(sel);
            if (step === 1) {
              const view = doc.defaultView || window;
              const styles = view.getComputedStyle(el);
              const existingChange = variationJson.find((v) => v.selector === sel);

              // When selector already has saved change, load that for editing.
              // Otherwise load the element's current values from the page.
              setContent(existingChange?.content ?? el.innerText ?? "");
              setTextColor(existingChange?.styles?.color ?? rgbToHex(styles.color));
              setBgColor(
                existingChange?.styles?.backgroundColor ?? rgbToHex(styles.backgroundColor)
              );
            }
            if (step === 2) setGoalSelector(sel);
          }
        },
        true
      );

      if (shouldApply) applyVariationToIframe(iframe, variationJson);
    };

    iframe.srcdoc = injectIntoHead(sanitizedHtml, `${baseTag}${EDITOR_PREVIEW_STYLE}`);
  } catch (err) {
    console.error("[LOAD] Error:", err);
    renderLoadError(iframe, pageUrl, err.message);
  }
};
